/**
 * OKS-405 — Crew motion vocabulary.
 *
 * A small, named set of animation "beats" so every celebration /
 * transition across the app speaks one consistent visual language
 * instead of being hand-rolled per feature. Each beat maps to a
 * Tailwind v4 `--animate-*` utility defined in
 * `app/frontend/entrypoints/application.css` (there is NO
 * `tailwind.config.js` in this project — keyframes live in the
 * `@theme inline` block). The reference doc lives at
 * `docs/frontend/motion.md`.
 *
 * # Hard constraint (OKS-405 Q-13) — rejected runtimes
 *
 * Motion in Crew is **CSS-only**. The following are explicitly
 * REJECTED and must never be added to the bundle:
 *
 *   - `framer-motion` (and any `motion/*` successor package)
 *   - `lottie-web` / `lottie-react` / any Lottie runtime
 *   - any other JS animation runtime
 *
 * Reasons: zero bundle cost, `prefers-reduced-motion` handled for free
 * via `motion-safe:`, and a single source of truth for easing/duration.
 * A Vitest guard (owned by LQE) fails the build if either dependency or
 * a source import appears.
 *
 * # Usage
 *
 * Import the beat's class and apply it — the `motion-safe:` prefix is
 * baked in, so reduced-motion users get no animation with no extra
 * work:
 *
 *   import { MOTION_BEATS } from "@/lib/motion"
 *   <EmptyState className={MOTION_BEATS["overlay-enter"]} … />
 *
 * # Beats (the ONLY sanctioned vocabulary — single-entrance only)
 *
 *   - `rank-up`          — rank-promotion entrance. Aliases the OKS-397
 *                          `rank-pop` keyframes (no duplicate keyframe).
 *   - `badge-earn`       — badge reveal pop (§14 badge earn modal).
 *   - `mission-accept`   — mission-acceptance confirm nudge (§08).
 *   - `content-approved` — content-approval settle (submission approved).
 *   - `overlay-enter`    — gentle fade + slight rise on mount. Wired on the
 *                          premium empty states + bespoke overlay mounts.
 *   - `brand-intro`      — OKS-457 §00 brand-splash logo entrance: opacity
 *                          0→1 + scale 0.96→1, single play. Wired live on
 *                          the public invite splash overlay.
 *   - `live-pulse`       — §06/§10/§16 single-entrance "something just went
 *                          live" beat (fade + 16px y-settle, ≤480ms). Played
 *                          once on a freshly-prepended cheer / pulse row /
 *                          live arrival. OKS-562 collapsed this to a
 *                          one-shot (it absorbed the OKS-473 `pulse-arrival`
 *                          beat) — it MUST NOT loop.
 *   - `drop-reveal`      — OKS-472 §05 one-time New-Drop card entrance.
 *
 * # Status affordances (NOT vocabulary beats — reject-list exception)
 *
 * OKS-562 §24 forbids loops on the vocabulary beats. The gently-breathing
 * "live" Crew Pulse dot is a *status indicator*, not a celebration/transition
 * beat, so it lives in a SEPARATE `STATUS_AFFORDANCES` map. Keeping it out of
 * `MOTION_BEATS` makes the "no beat loops" invariant true by construction
 * (LQE's `motion-vocabulary.test.ts` guard asserts only `live-status` is
 * `infinite`).
 */
export const MOTION_BEATS = {
  "rank-up": "motion-safe:animate-rank-up",
  "badge-earn": "motion-safe:animate-badge-earn",
  "mission-accept": "motion-safe:animate-mission-accept",
  "content-approved": "motion-safe:animate-content-approved",
  "overlay-enter": "motion-safe:animate-overlay-enter",
  "brand-intro": "motion-safe:animate-brand-intro",
  // OKS-473/OKS-562 — §06 single-entrance live-arrival beat (fade + 16px
  // y-settle, ≤480ms). One-shot: played once on a freshly-prepended row.
  "live-pulse": "motion-safe:animate-live-pulse",
  // OKS-472 — §05 Crew Home one-time New-Drop card entrance on mount.
  "drop-reveal": "motion-safe:animate-drop-reveal",
} as const

/** A named motion beat in the Crew vocabulary. */
export type MotionBeat = keyof typeof MOTION_BEATS

/**
 * OKS-562 — status-indicator affordances. NOT motion vocabulary beats: these
 * are subtle, continuous status cues (the reject-list's permitted exception),
 * kept out of `MOTION_BEATS` so the "no beat loops" invariant holds by
 * construction. Consumed via `motion-safe:` like the beats, so reduced-motion
 * users get a static indicator.
 *
 *   - `live-status` — the Crew Pulse "live" dot: a gentle, infinite
 *                     scale+opacity breath (was the pre-OKS-562 looping
 *                     `live-pulse`; renamed so `live-pulse` can mean a
 *                     one-shot arrival beat).
 */
export const STATUS_AFFORDANCES = {
  "live-status": "motion-safe:animate-live-status",
} as const

/** A named status-indicator affordance (not a vocabulary beat). */
export type StatusAffordance = keyof typeof STATUS_AFFORDANCES

/**
 * Animation runtimes explicitly rejected for Crew (OKS-405 Q-13).
 * Exported so the dependency guard can assert against a single list.
 */
export const REJECTED_MOTION_RUNTIMES = [
  "framer-motion",
  "motion",
  "lottie-web",
  "lottie-react",
] as const
